import { Alert, Button, Card, CardContent, Spinner, StatTile, buttonVariants, cn } from '@blink/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Banknote, ChevronRight, CircleHelp, LogOut, MapPin, PackageSearch, Route, ScanLine, CloudOff } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { ErrorAlert, Screen } from '@/components/common';
import { LiveDot, StatusBadge, aed } from '@/components/parcel-bits';
import { GoButton, NavAppPicker, currentLocation, useNavApp } from '@/components/route-bits';
import { useI18n } from '@/i18n';
import { api, type CourierDay, type CourierMe, type DayParcel, type MyCash } from '@/lib/api';
import { useLiveUpdates } from '@/lib/live';
import { discardOutboxItem, flushOutbox, useOutbox, type OutboxItem } from '@/lib/outbox';
import { ShiftCard } from '@/components/shift';
import { stopTracking } from '@/lib/gps';
import { HelpTip } from '@/components/help-tip';

/** Screens 1 and 2: the day's summary, next stop, and parcels by district in route order. */
export function Home() {
  const { t, f, formatDate, formatTime } = useI18n();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const live = useLiveUpdates();
  const me = useQuery({ queryKey: ['courier', 'me'], queryFn: () => api<CourierMe>('/courier/me') });
  // no timer: the list reloads when the server says the work changed (live updates)
  const day = useQuery({ queryKey: ['courier', 'day'], queryFn: () => api<CourierDay>('/courier/day'), refetchOnWindowFocus: true });
  const cash = useQuery({ queryKey: ['courier', 'cash'], queryFn: () => api<MyCash>('/courier/cash') });
  const signOut = useMutation({
    mutationFn: () => api<void>('/auth/logout', { method: 'POST' }),
    onSettled: () => {
      stopTracking();
      // the next driver on this phone must not see this driver's day
      queryClient.clear();
      navigate('/login?reason=signedout', { replace: true });
    },
  });
  // weak signal: actions still on the phone; a parcel delivered or failed there is finished for the courier
  const outbox = useOutbox();
  const finished = new Set(outbox.filter((x) => x.type === 'DELIVER' || x.type === 'FAIL').map((x) => x.shipmentId));
  const [signOutBlocked, setSignOutBlocked] = useState(false);
  const s = day.data?.summary;
  const route = day.data?.route ?? null;
  const onRoute = (p: DayParcel) => p.status === 'IN_TRANSIT' || p.status === 'OUT_FOR_DELIVERY';
  const next = day.data
    ? day.data.next && !finished.has(day.data.next.id)
      ? day.data.next
      : (day.data.districts.flatMap((g) => g.parcels).find((p) => onRoute(p) && !finished.has(p.id)) ?? null)
    : null;
  const [navApp, setNavApp] = useNavApp();
  const queryClient = useQueryClient();
  const [located, setLocated] = useState<boolean | null>(null);
  const reorder = useMutation({
    mutationFn: async () => {
      const location = await currentLocation();
      setLocated(!!location);
      return api('/courier/route/reorder', { method: 'POST', body: { location } });
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['courier'] }),
  });

  return (
    <Screen
      title={
        <>
          <p className="flex items-center gap-3 text-xs text-sidebar-foreground">
            {me.data ? formatDate(me.data.today) : t.home.today}
            {me.data?.hub ? <span data-testid="courier-hub">{me.data.hub.name}</span> : null}
            <LiveDot state={live} />
          </p>
          <h1 className="truncate text-lg font-semibold" data-testid="courier-name">
            {me.data ? f(t.home.hello, { name: me.data.courier.name }) : t.app.name}
          </h1>
        </>
      }
      actions={
        <>
        <Button variant="ghost" size="icon" className="text-white hover:bg-white/10 hover:text-white" aria-label={t.menu.help} onClick={() => navigate('/help')} data-testid="open-help">
          <CircleHelp className="h-5 w-5" />
        </Button>
        <Button variant="ghost" size="icon" className="text-white hover:bg-white/10 hover:text-white" aria-label={t.menu.signOut} onClick={() => (outbox.length ? setSignOutBlocked(true) : signOut.mutate())} data-testid="sign-out">
          <LogOut className="h-5 w-5" />
        </Button>
        </>
      }
    >
      <ErrorAlert error={me.error ?? day.error} />
      {signOutBlocked && outbox.length ? <Alert variant="warning" data-testid="sign-out-blocked">{t.outbox.signOutBlocked}</Alert> : null}
      <OutboxBanner items={outbox} />
      <ShiftCard />
      {day.isLoading ? <Spinner /> : null}
      {params.get('started') ? (
        <Alert variant="success" data-testid="route-started">
          <p className="font-semibold">{f(t.home.started, { out: params.get('started') ?? 0 })}</p>
          {Number(params.get('left')) > 0 ? <p className="text-sm">{f(t.home.startedLeft, { left: params.get('left') ?? 0 })}</p> : null}
        </Alert>
      ) : null}

      {s ? (
        <div className="grid grid-cols-2 gap-3" data-testid="day-summary">
          <StatTile label={t.home.parcels} value={<span data-testid="sum-parcels">{s.parcels}</span>} hint={f(t.home.pieces, { count: s.pieces })} />
          <StatTile label={t.home.cod} value={<span className="text-lg" data-testid="sum-cod">{aed(s.codFils)}</span>} tone="amber" />
          <StatTile label={t.home.toLoad} value={<span data-testid="sum-to-load">{s.toLoad}</span>} hint={t.home.loadHint} tone="blue" />
          <StatTile label={t.home.done} value={s.delivered + s.failed} hint={f(t.home.doneHint, { delivered: s.delivered, failed: s.failed })} tone="green" />
        </div>
      ) : null}

      {cash.data ? (
        <Link to="/cash" data-testid="go-cash" className={cn('flex items-center gap-3 rounded-xl border p-3 text-sm font-semibold', cash.data.earlierFils > 0 && !cash.data.routeStart.exception ? 'border-destructive/40 bg-destructive/5 text-destructive' : 'bg-card')}>
          <Banknote className="h-5 w-5 shrink-0" />
          <span className="flex-1">{f(t.cash.homeLink, { amount: aed(cash.data.inHandFils) })}</span>
          <ChevronRight className="h-4 w-4 text-muted-foreground" />
        </Link>
      ) : null}

      {s && (s.toLoad > 0 || s.loaded > 0) ? (
        <Link to="/load" data-testid="go-load" className={cn(buttonVariants(), 'h-14 w-full text-base')}>
          <ScanLine className="h-5 w-5" />
          {s.toLoad > 0 ? f(t.home.scanLoad, { count: s.toLoad }) : f(t.home.startLoaded, { count: s.loaded })}
        </Link>
      ) : null}

      {day.data && s && s.parcels > 0 ? (
        <section className="space-y-2">
          <h2 className="text-sm font-semibold text-muted-foreground">{t.home.next}</h2>
          {next ? (
            <>
              <ParcelRow parcel={next} big />
              <GoButton key={next.id} shipmentId={next.id} navApp={navApp} />
            </>
          ) : (
            <p className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground">{t.home.noNext}</p>
          )}
        </section>
      ) : null}

      {route && s && s.onRoute > 0 ? (
        <Card data-testid="route-card">
          <CardContent className="space-y-3 pt-5">
            <div className="flex items-start gap-2">
              <Route className="mt-0.5 h-5 w-5 text-muted-foreground" />
              <div className="min-w-0 flex-1">
                <p className="font-semibold" data-testid="route-summary">
                  {f(t.route.summary, { km: route.km, time: formatTime(route.finishAt) })}
                </p>
                <p className="text-xs text-muted-foreground">
                  {route.from === 'GPS' ? t.route.fromHere : t.route.fromHub}
                  {route.mode !== 'OSRM' ? ` · ${t.route.straight}` : ''}
                </p>
              </div>
            </div>
            {route.outdated ? <Alert variant="warning" data-testid="route-outdated">{t.route.outdated}</Alert> : null}
            {located === false ? <p className="text-xs text-warning-foreground">{t.route.noLocation}</p> : null}
            <div className="flex flex-wrap items-center justify-between gap-2">
              <Button variant="outline" className="h-11" disabled={reorder.isPending} onClick={() => reorder.mutate()} data-testid="reorder">
                <Route className="h-4 w-4" />
                {reorder.isPending ? t.route.reordering : t.route.reorder}
              </Button>
              <NavAppPicker value={navApp} onChange={setNavApp} />
            </div>
            <ErrorAlert error={reorder.error} />
          </CardContent>
        </Card>
      ) : null}

      {day.data && s?.parcels === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-2 py-10 text-center" data-testid="day-empty">
            <PackageSearch className="h-10 w-10 text-muted-foreground" />
            <h2 className="font-semibold">{t.home.emptyTitle}</h2>
            <p className="text-sm text-muted-foreground">{t.home.emptyText}</p>
          </CardContent>
        </Card>
      ) : null}

      {day.data && s && s.parcels > 0 ? (
        <section className="space-y-3" data-testid="day-districts">
          <h2 className="text-sm font-semibold text-muted-foreground">{t.home.allParcels}</h2>
          {day.data.districts.map((g, i) => (
            <div key={`${i}-${g.name ?? '-'}`} className="space-y-2" data-testid="district-group">
              <h3 className="flex items-center gap-1.5 text-sm font-semibold">
                <MapPin className="h-4 w-4 text-muted-foreground" />
                {g.name ?? t.home.noDistrict}
                <span className="font-normal text-muted-foreground">· {g.parcels.length}</span>
              </h3>
              {g.parcels.map((p) => (
                <ParcelRow key={p.id} parcel={p} kept={finished.has(p.id)} />
              ))}
            </div>
          ))}
        </section>
      ) : null}
    </Screen>
  );
}

/** One line of the list: only what the courier needs; details open on tap. */
function ParcelRow({ parcel: p, big = false, kept = false }: { parcel: DayParcel; big?: boolean; kept?: boolean }) {
  const { t, f, formatTime } = useI18n();
  return (
    <Link
      to={`/parcels/${p.id}`}
      className={`flex items-center gap-3 rounded-xl border bg-card px-4 py-3 shadow-sm active:bg-muted ${big ? 'border-primary/40' : ''}`}
      data-testid="parcel-row"
      data-awb={p.awb}
    >
      <div className="min-w-0 flex-1 space-y-1">
        <div className="flex items-center gap-2">
          {p.stop ? (
            <span className="flex h-6 min-w-6 items-center justify-center rounded-full bg-sidebar px-1.5 text-xs font-bold text-white" aria-label={f(t.route.stop, { n: p.stop })} data-testid="stop-number">
              {p.stop}
            </span>
          ) : null}
          <span className="font-mono text-sm font-semibold">{p.awb}</span>
          {kept ? (
            <span className="rounded-full bg-warning-soft px-2 py-0.5 text-xs font-semibold text-warning-foreground" data-testid="parcel-kept">
              {t.outbox.kept}
            </span>
          ) : (
            <StatusBadge status={p.status} loaded={p.loaded} />
          )}
        </div>
        <p className="truncate text-sm">
          {p.consignee}
          {big && p.district ? <span className="text-muted-foreground"> · {p.district}</span> : null}
        </p>
        <p className="text-xs text-muted-foreground">
          {p.pieceCount === 1 ? t.parcel.onePiece : f(t.parcel.pieces, { count: p.pieceCount })}
          {' · '}
          {p.codFils > 0 ? f(t.parcel.cod, { amount: aed(p.codFils) }) : t.parcel.noCod}
          {p.timeSlot ? ` · ${p.timeSlot}` : ''}
          {p.etaAt ? ` · ${f(t.route.eta, { time: formatTime(p.etaAt) })}` : ''}
          {p.carriedOver ? <span className="text-warning-foreground"> · {t.parcel.carriedOver}</span> : null}
        </p>
      </div>
      <ChevronRight className="h-5 w-5 shrink-0 text-muted-foreground" />
    </Link>
  );
}

/** Actions kept on the phone (weak signal): how many wait, and the ones the office refused. */
function OutboxBanner({ items }: { items: OutboxItem[] }) {
  const { t, f } = useI18n();
  const waiting = items.filter((x) => x.state === 'WAITING');
  const refused = items.filter((x) => x.state === 'FAILED');
  if (!items.length) return null;
  return (
    <div className="space-y-2">
      {waiting.length ? (
        <Alert variant="warning" data-testid="outbox-banner">
          <div className="flex items-center gap-2">
            <CloudOff className="h-4 w-4 shrink-0" />
            <span className="flex-1 font-semibold">{waiting.length === 1 ? t.outbox.waitingOne : f(t.outbox.waiting, { count: waiting.length })}</span>
            <Button size="sm" variant="outline" onClick={() => void flushOutbox()} data-testid="outbox-send">
              {t.outbox.sendNow}
            </Button>
          </div>
        </Alert>
      ) : null}
      {refused.map((x) => (
        <Alert key={x.id} variant="destructive" data-testid="outbox-refused">
          <p className="font-semibold">
            {t.outbox.refused} <span className="font-mono">{x.awb}</span> · {t.outbox.types[x.type]}
          </p>
          <p className="text-sm">{t.errors[x.error?.code as keyof typeof t.errors] ?? x.error?.message}</p>
          <Button size="sm" variant="outline" className="mt-2" onClick={() => void discardOutboxItem(x.id)}>
            {t.outbox.discard}
          </Button>
        </Alert>
      ))}
    </div>
  );
}
